import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { GAME_PARAM, gameSlug } from "../../shared/games";
import { useI18n } from "../i18n/I18nProvider";
import { useGame } from "../lib/GameProvider";

/** What a list's address can't keep across games: sets, rarities and finishes are one game's, and the page restarts. */
const GAME_BOUND_PARAMS = [GAME_PARAM, "set", "rareza", "acabado", "pagina", "producto"];

/** The address without what belongs to the game left behind. */
export function withoutGameParams(search: string): string {
  const sp = new URLSearchParams(search);
  for (const k of GAME_BOUND_PARAMS) sp.delete(k);
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/**
 * A link with ?juego= (a shared one, an ad) switches to that game and remembers it, then leaves the
 * address: the header's switcher is the one place the choice lives.
 */
export function GameFromUrl() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { setGame } = useGame();
  useEffect(() => {
    const sp = new URLSearchParams(search);
    if (!sp.has(GAME_PARAM)) return;
    const slug = gameSlug(sp.get(GAME_PARAM));
    if (slug) setGame(slug);
    sp.delete(GAME_PARAM);
    const qs = sp.toString();
    navigate({ pathname, search: qs ? `?${qs}` : "", hash }, { replace: true });
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** The header's game picker; only once there is more than one game to pick. */
export function GameSwitcher({ className = "" }: { className?: string }) {
  const { t, locale } = useI18n();
  const { game, games, setGame } = useGame();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  if (games.length < 2) return null;
  const pick = (slug: string) => {
    if (slug === game) return;
    setGame(slug);
    const next = withoutGameParams(search);
    if (next !== search) navigate({ pathname, search: next, hash }, { replace: true });
  };
  return (
    <label className={`game-switch ${className}`.trim()}>
      <span className="sr-only">{t("game.label")}</span>
      <select value={game} onChange={(e) => pick(e.target.value)} aria-label={t("game.label")}>
        {games.map((g) => (
          <option key={g.slug} value={g.slug}>
            {g.name[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
